/**
 * Make any attachment URL render in-browser when the type supports it.
 *
 * Browsers render PDFs, images, and text inline natively but can't
 * display Microsoft Office formats — those default to download. We
 * wrap office types in Microsoft's free Office Online viewer
 * (https://view.officeapps.live.com/op/view.aspx?src=<urlencoded url>),
 * which fetches the file and renders it in a browser tab.
 *
 * Used everywhere the frontend renders a raw Freshservice signed URL
 * (Helpdesk inbox AttachmentChip, Service Desk ticket detail pane).
 * The /open backend redirect performs the same wrap server-side for
 * Resource-tab attachments — using this helper at the FS-direct
 * surfaces keeps every attachment-click behavior consistent.
 */

const OFFICE_MIME_PREFIXES = [
  "application/vnd.openxmlformats-officedocument.",
  "application/vnd.ms-",
] as const;
const OFFICE_MIME_EXACT = new Set([
  "application/msword",
]);

const OFFICE_EXTENSIONS = new Set([
  "doc", "docx", "xls", "xlsx", "ppt", "pptx",
]);

function isOfficeDoc(filename: string | null | undefined, contentType: string | null | undefined): boolean {
  const ct = (contentType ?? "").toLowerCase();
  if (OFFICE_MIME_EXACT.has(ct)) return true;
  if (OFFICE_MIME_PREFIXES.some((p) => ct.startsWith(p))) return true;
  // Fall back to extension when content_type is missing/wrong (FS doesn't
  // always report a useful MIME for older doc types).
  const dot = (filename ?? "").lastIndexOf(".");
  if (dot >= 0) {
    const ext = (filename ?? "").slice(dot + 1).toLowerCase();
    if (OFFICE_EXTENSIONS.has(ext)) return true;
  }
  return false;
}

/** Returns the href to render in <a> for an attachment. */
export function attachmentHref(
  url: string,
  options: { filename?: string | null; contentType?: string | null } = {},
): string {
  if (!url || !/^https?:\/\//i.test(url)) return url;
  if (!isOfficeDoc(options.filename, options.contentType)) return url;
  return (
    "https://view.officeapps.live.com/op/view.aspx?src=" +
    encodeURIComponent(url)
  );
}
